import { useEffect, useMemo, useState } from "react";
import { api, apiErrToStr, fmtDate, fmtDuration } from "@/lib/api";
import {
  Activity,
  Clock3,
  Loader2,
  LogIn,
  LogOut,
  Pause,
  PhoneCall,
  RefreshCw,
  Users,
  PhoneForwarded,
  BadgeCheck,
  TimerReset,
  PhoneIncoming,
} from "lucide-react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const numberKeys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

const agentStatusMeta = {
  available: { label: "Online", className: "tag-online" },
  on_break: { label: "Em pausa", className: "tag-warn" },
  busy: { label: "Em atendimento", className: "tag-online" },
  offline: { label: "Offline", className: "tag-offline" },
};

const finishedWaitingStates = new Set([
  "abandoned",
  "answered",
  "bridge",
  "bridged",
  "cancelled",
  "canceled",
  "completed",
  "destroyed",
  "hangup",
  "leaving",
  "terminated",
]);

function MetricCard({ icon: Icon, label, value, detail, tone = "default" }) {
  const toneClass = {
    default: "text-[color:var(--text-primary)]",
    good: "text-[color:var(--accent-green)]",
    info: "text-[color:var(--accent-blue)]",
    warn: "text-[color:var(--accent-amber)]",
    danger: "text-[color:var(--accent-red)]",
  }[tone] || "text-[color:var(--text-primary)]";
  return (
    <div className="sbc-card p-3 min-h-[88px] flex flex-col justify-between">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{label}</div>
        <Icon size={14} className={toneClass} />
      </div>
      <div>
        <div className={`font-mono text-2xl leading-none font-semibold ${toneClass}`}>{value}</div>
        {detail ? <div className="mt-1 text-[11px] leading-tight text-[color:var(--text-secondary)]">{detail}</div> : null}
      </div>
    </div>
  );
}

function Panel({ title, subtitle, children, actions }) {
  return (
    <div className="sbc-card overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-[color:var(--border-default)] px-3 py-2.5">
        <div>
          <div className="text-[11px] font-semibold">{title}</div>
          {subtitle ? <div className="text-[10px] text-[color:var(--text-muted)]">{subtitle}</div> : null}
        </div>
        {actions}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function AgentPill({ status }) {
  const meta = agentStatusMeta[status] || agentStatusMeta.offline;
  return <span className={`sbc-tag ${meta.className}`}>{meta.label}</span>;
}

export default function AgentConsole() {
  const [dashboard, setDashboard] = useState(null);
  const [dialValue, setDialValue] = useState("");
  const [selectedExtension, setSelectedExtension] = useState("");
  const [selectedPause, setSelectedPause] = useState("");
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data: nextDashboard } = await api.get("/callcenter/agent/dashboard");
      setDashboard(nextDashboard);
      setSelectedExtension((current) => current || nextDashboard?.agent?.active_extension || "");
      setSelectedPause((current) => current || nextDashboard?.pause_reasons?.[0]?.id || "");
      setError("");
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const timer = setInterval(() => load(true), 7000);
    return () => clearInterval(timer);
  }, []);

  const currentAgent = dashboard?.agent || {};
  const selectedPauseReason = (dashboard?.pause_reasons || []).find((reason) => reason.id === selectedPause);
  const queueRows = dashboard?.queues || [];
  const waitingRows = useMemo(() => (
    queueRows.flatMap((queue) =>
      (queue.callers || [])
        .filter((caller) => !finishedWaitingStates.has(String(caller?.state || "waiting").trim().toLowerCase()))
        .map((caller) => ({
          ...caller,
          queue_number: queue.number,
          queue_name: queue.name,
        })),
    )
  ), [queueRows]);
  const groupAgents = dashboard?.group_agents || [];
  const selectedExtensionOptions = [];

  const queueChart = useMemo(() => (
    queueRows.map((queue) => ({
      fila: queue.name || queue.number,
      aguardando: queue.waiting_count || 0,
    }))
  ), [queueRows]);
  const broadcastText = dashboard?.broadcast?.ativo && String(dashboard?.broadcast?.mensagem || "").trim()
    ? String(dashboard.broadcast.mensagem).trim()
    : "";
  const broadcastDisabledMessage = !broadcastText
    ? (String(dashboard?.broadcast?.mensagem || "").trim()
        ? "Letreiro desativado pela supervisao."
        : "Nenhum letreiro publicado para este cliente.")
    : "";

  const hourlyChart = useMemo(() => (
    (dashboard?.hourly || []).map((row) => ({
      hora: row.hour,
      Atendidas: row.answered || 0,
      "Nao atendidas": row.missed || 0,
    }))
  ), [dashboard]);

  const pushDial = (value) => setDialValue((current) => `${current}${value}`);
  const backspaceDial = () => setDialValue((current) => current.slice(0, -1));
  const clearDial = () => setDialValue("");

  const runAction = async (key, fn) => {
    setBusy(key);
    setError("");
    setSuccess("");
    try {
      await fn();
      await load(true);
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  };

  const handleLogin = async () => {
    if (!currentAgent.id) return;
    if (!selectedExtension) {
      setError("Informe o ramal de operacao antes do login.");
      return;
    }
    await runAction("login", async () => {
      await api.put(`/callcenter/agents/${currentAgent.id}/extension`, { extension: selectedExtension });
      await api.post(`/callcenter/agents/${currentAgent.id}/login`);
      setSuccess(`Agente logado no ramal ${selectedExtension}.`);
    });
  };

  const handlePause = async () => {
    if (!currentAgent.id) return;
    await runAction("pause", async () => {
      await api.post(`/callcenter/agents/${currentAgent.id}/break`, { reason_id: selectedPause || undefined });
      const reason = (dashboard?.pause_reasons || []).find((row) => row.id === selectedPause);
      setSuccess(`Pausa aplicada${reason ? `: ${reason.nome}` : ""}.`);
    });
  };

  const handleLogout = async () => {
    if (!currentAgent.id) return;
    await runAction("logout", async () => {
      await api.post(`/callcenter/agents/${currentAgent.id}/logout`);
      setSuccess("Agente deslogado.");
    });
  };

  const handleDial = async () => {
    if (!dialValue.trim()) {
      setError("Digite o numero ou destino para discar.");
      return;
    }
    await runAction("dial", async () => {
      const { data } = await api.post("/callcenter/agent/dial", { destination: dialValue.trim() });
      setSuccess(data?.message || "O ramal vai tocar para completar a chamada.");
    });
  };

  const handleCapture = async (row) => {
    await runAction(`capture-${row.session_uuid}`, async () => {
      await api.post("/callcenter/agent/capture", {
        queue_number: row.queue_number,
        session_uuid: row.session_uuid,
      });
      setSuccess(`Ligacao da fila ${row.queue_number} enviada para o seu ramal.`);
    });
  };

  return (
    <div className="space-y-4" data-testid="agent-console-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Operacao em tempo real</div>
          <h1 className="text-xl font-semibold tracking-tight">Painel do Agente</h1>
          <div className="mt-1.5 flex items-center gap-2 text-sm">
            <span className="font-medium">{currentAgent.name || "Agente"}</span>
            <AgentPill status={currentAgent.status} />
            {currentAgent.active_extension ? (
              <span className="font-mono text-[11px] text-[color:var(--text-muted)]">Ramal: {currentAgent.active_extension}</span>
            ) : null}
          </div>
        </div>
        <button className="sbc-btn" onClick={() => load()} disabled={loading || !!busy}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
        </button>
      </div>

      {broadcastText ? (
        <section className="overflow-hidden border border-emerald-200 bg-emerald-50/85">
          <div className="px-3 py-2 flex items-center gap-3 text-emerald-800">
            <span className="text-[10px] uppercase tracking-widest font-semibold shrink-0">Informacoes da supervisao</span>
            <div className="voxyra-broadcast-track flex-1">
              <div className="voxyra-broadcast-marquee font-mono text-sm">
                {broadcastText}
              </div>
            </div>
          </div>
        </section>
      ) : broadcastDisabledMessage ? (
        <section className="overflow-hidden border border-amber-200 bg-amber-50/85">
          <div className="px-3 py-2 flex items-center gap-3 text-amber-900">
            <span className="text-[10px] uppercase tracking-widest font-semibold shrink-0">Informacoes da supervisao</span>
            <div className="text-xs font-medium">{broadcastDisabledMessage}</div>
          </div>
        </section>
      ) : null}

      {error ? <div className="text-xs text-[color:var(--accent-red)] font-mono border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] px-3 py-2 rounded-sm">! {error}</div> : null}
      {success ? <div className="text-xs text-[color:var(--accent-green)] font-mono border border-[rgba(34,197,94,0.3)] bg-[rgba(34,197,94,0.08)] px-3 py-2 rounded-sm">{success}</div> : null}

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-[348px_minmax(0,1fr)]">
        <div className="space-y-3">
          <Panel title="Discador" subtitle={`Ramal ${currentAgent.active_extension || selectedExtension || "--"}`}>
            <div className="space-y-2.5">
              <div className="flex items-center gap-2">
                <input
                  className="sbc-input flex-1 font-mono"
                  value={dialValue}
                  onChange={(event) => setDialValue(event.target.value)}
                  placeholder="Numero ou destino"
                />
                <button className="sbc-btn" type="button" onClick={backspaceDial} title="Apagar ultimo digito">&larr;</button>
                <button className="sbc-btn" type="button" onClick={clearDial} title="Limpar">X</button>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {numberKeys.map((key) => (
                  <button key={key} type="button" className="sbc-btn h-9 font-mono text-sm" onClick={() => pushDial(key)}>
                    {key}
                  </button>
                ))}
              </div>
              <button className="sbc-btn sbc-btn-primary w-full justify-center" type="button" disabled={!!busy} onClick={handleDial}>
                {busy === "dial" ? <Loader2 size={14} className="animate-spin" /> : <PhoneCall size={14} />}
                Ligar
              </button>
            </div>
          </Panel>

          <Panel title="Operacao do agente" subtitle="Login, pausa e deslogar">
            <div className="space-y-2.5">
              <div className="grid grid-cols-[minmax(0,1fr)_40px] gap-2">
                <div>
                  <label className="sbc-label">Ramal de operacao</label>
                  <input
                    list="agent-extension-options"
                    className="sbc-input mt-1 font-mono"
                    value={selectedExtension}
                    onChange={(event) => setSelectedExtension(event.target.value)}
                    placeholder="9165"
                  />
                  <datalist id="agent-extension-options">
                    {selectedExtensionOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </datalist>
                </div>
                <button
                  className="sbc-btn mt-[22px]"
                  type="button"
                  disabled={!!busy}
                  onClick={() => runAction("extension", async () => {
                    if (!selectedExtension) throw new Error("Informe o ramal de operacao.");
                    await api.put(`/callcenter/agents/${currentAgent.id}/extension`, { extension: selectedExtension });
                    setSuccess(`Ramal operacional alterado para ${selectedExtension}.`);
                  })}
                  title="Salvar ramal"
                >
                  {busy === "extension" ? <Loader2 size={14} className="animate-spin" /> : <BadgeCheck size={14} />}
                </button>
              </div>
              <div>
                <label className="sbc-label">Motivo da pausa</label>
                <select className="sbc-input mt-1" value={selectedPause} onChange={(event) => setSelectedPause(event.target.value)}>
                  {(dashboard?.pause_reasons || []).map((reason) => (
                    <option key={reason.id} value={reason.id} disabled={!reason.available}>
                      {reason.nome}{reason.limite_minutos ? ` (${fmtDuration(reason.remaining_seconds || 0)} restantes)` : ""}{!reason.available ? " - limite usado" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-wrap gap-2">
                <button className="sbc-btn sbc-btn-primary" type="button" disabled={!!busy} onClick={handleLogin}>
                  {busy === "login" ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />}
                  Login
                </button>
                <button className="sbc-btn" type="button" disabled={!!busy || selectedPauseReason?.available === false} onClick={handlePause}>
                  {busy === "pause" ? <Loader2 size={14} className="animate-spin" /> : <Pause size={14} />}
                  Pausa
                </button>
                <button className="sbc-btn sbc-btn-danger" type="button" disabled={!!busy} onClick={handleLogout}>
                  {busy === "logout" ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />}
                  Deslogar
                </button>
              </div>
              {currentAgent.pause_reason_name ? (
                <div className="text-xs text-[color:var(--text-secondary)]">
                  Pausa atual: <span className="font-medium">{currentAgent.pause_reason_name}</span>
                  {currentAgent.pause_deadline_at ? <span> · saldo ate {fmtDate(currentAgent.pause_deadline_at)}</span> : null}
                </div>
              ) : null}
            </div>
          </Panel>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <MetricCard icon={Clock3} label="Chamadas em espera" value={dashboard?.waiting_total || 0} tone={(dashboard?.waiting_total || 0) > 0 ? "warn" : "default"} />
            <MetricCard icon={PhoneIncoming} label="Atendidas hoje" value={dashboard?.summary?.answered || 0} tone="good" />
            <MetricCard icon={Activity} label="Nao atendidas" value={dashboard?.summary?.abandoned || 0} tone={(dashboard?.summary?.abandoned || 0) > 0 ? "danger" : "default"} />
            <MetricCard icon={Users} label="Agentes online" value={dashboard?.online_agents || 0} tone="info" />
          </div>

          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            <Panel title="Chamadas aguardando por fila">
              <div className="h-[190px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={queueChart} margin={{ top: 8, right: 12, bottom: 0, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.18)" />
                    <XAxis dataKey="fila" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="aguardando" stroke="#4f7cff" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel title="Meu desempenho por hora">
              <div className="h-[190px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={hourlyChart} margin={{ top: 8, right: 12, bottom: 0, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.18)" />
                    <XAxis dataKey="hora" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="Atendidas" stroke="#19c37d" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="Nao atendidas" stroke="#ef4444" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>
        </div>
      </div>

      <Panel title={`Ligacoes em espera ${waitingRows.length ? `(${waitingRows.length})` : ""}`}>
        <div className="overflow-auto">
          <table className="sbc-table">
            <thead>
              <tr>
                <th>Fila</th>
                <th>Numero</th>
                <th>Nome</th>
                <th>Tempo de espera</th>
                <th>Estado</th>
                <th className="text-right">Acao</th>
              </tr>
            </thead>
            <tbody>
              {!waitingRows.length ? (
                <tr><td colSpan={6} className="py-8 text-center text-[color:var(--text-muted)]">Nenhuma chamada aguardando nas filas do agente.</td></tr>
              ) : waitingRows.map((row) => (
                <tr key={row.session_uuid || row.uuid}>
                  <td className="font-mono">{row.queue_number}</td>
                  <td className="font-mono">{row.caller_number || "-"}</td>
                  <td>{row.caller_name || "-"}</td>
                  <td className="font-mono">{fmtDuration(row.wait_sec || 0)}</td>
                  <td>{row.state || "Waiting"}</td>
                  <td className="text-right">
                    <button
                      className="sbc-btn sbc-btn-primary"
                      type="button"
                      disabled={!!busy}
                      onClick={() => handleCapture(row)}
                    >
                      {busy === `capture-${row.session_uuid}` ? <Loader2 size={14} className="animate-spin" /> : <PhoneForwarded size={14} />}
                      Capturar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Agentes do meu grupo">
        <div className="overflow-auto">
          <table className="sbc-table">
            <thead>
              <tr>
                <th>Agente</th>
                <th>Ramal em uso</th>
                <th>Filas</th>
                <th>Estado</th>
                <th>Ultima pausa</th>
              </tr>
            </thead>
            <tbody>
              {!groupAgents.length ? (
                <tr><td colSpan={5} className="py-8 text-center text-[color:var(--text-muted)]">Nenhum agente encontrado no grupo.</td></tr>
              ) : groupAgents.map((agent) => (
                <tr key={agent.id}>
                  <td>
                    <div className="font-medium">{agent.nome || agent.agente}</div>
                    <div className="font-mono text-[10px] text-[color:var(--text-muted)]">{agent.agente}</div>
                  </td>
                  <td className="font-mono">{agent.active_extension || agent.ramal || "-"}</td>
                  <td className="font-mono">{(agent.filas || []).join(", ") || "-"}</td>
                  <td><AgentPill status={agent.status} /></td>
                  <td className="text-xs text-[color:var(--text-secondary)]">{agent.pause_started_at ? fmtDate(agent.pause_started_at) : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
